/**
 * 利润推演表格列定义
 */
import React from 'react';
import { InputNumber, Typography, Tooltip, Tag } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { ScenarioData } from './types';
import { calculateMetrics } from './calculations';

const { Text } = Typography;

export const createColumns = (
    price: number,
    profitMarginPreAd: number,
    landedCost: number,
    onChange: (key: string, field: keyof ScenarioData, value: any) => void
): ColumnsType<ScenarioData> => {
    return [
        {
            title: '用途 / 场景',
            dataIndex: 'name',
            key: 'name',
            width: 160,
            fixed: 'left',
            render: (text) => <Text strong style={{ fontSize: 14 }}>{text}</Text>
        },
        {
            title: '基础设定',
            children: [
                {
                    title: '日销',
                    dataIndex: 'dailyUnits',
                    key: 'dailyUnits',
                    width: 70,
                    render: (v, r) => <InputNumber size="small" min={0} value={v} onChange={val => onChange(r.key, 'dailyUnits', val)} style={{ width: '100%' }} />
                },
                {
                    title: '天数',
                    dataIndex: 'days',
                    key: 'days',
                    width: 60,
                    render: (v, r) => <InputNumber size="small" min={1} value={v} onChange={val => onChange(r.key, 'days', val)} style={{ width: '100%' }} />
                },
            ]
        },
        {
            title: '广告参数',
            children: [
                {
                    title: <Tooltip title="广告出单占比"><span>广告占比% <InfoCircleOutlined /></span></Tooltip>,
                    dataIndex: 'tacosTarget',
                    key: 'tacosTarget',
                    width: 95,
                    render: (v, r) => {
                        const isReadOnly = r.key === '2' || r.key === '4';
                        return (
                            <InputNumber
                                size="small"
                                min={0}
                                max={100}
                                value={v}
                                onChange={val => onChange(r.key, 'tacosTarget', val)}
                                style={{
                                    width: '100%',
                                    backgroundColor: isReadOnly ? '#f5f5f5' : '#fffb8f',
                                    color: isReadOnly ? '#8c8c8c' : undefined
                                }}
                                suffix="%"
                                readOnly={isReadOnly}
                            />
                        );
                    }
                },
                {
                    title: '转化率%',
                    dataIndex: 'cvr',
                    key: 'cvr',
                    width: 80,
                    render: (v, r) => {
                        const isReadOnly = r.key === '6';
                        return (
                            <InputNumber
                                size="small"
                                min={0.1}
                                max={100}
                                value={v}
                                onChange={val => onChange(r.key, 'cvr', val)}
                                style={{
                                    width: '100%',
                                    backgroundColor: isReadOnly ? '#f5f5f5' : undefined,
                                    color: isReadOnly ? '#8c8c8c' : undefined
                                }}
                                suffix="%"
                                readOnly={isReadOnly}
                            />
                        );
                    }
                },
                {
                    title: 'CPC($)',
                    dataIndex: 'cpc',
                    key: 'cpc',
                    width: 80,
                    render: (v, r) => {
                        const isReadOnly = r.key === '5' || r.key === '7';
                        return (
                            <InputNumber
                                size="small"
                                min={0}
                                step={0.01}
                                value={v}
                                onChange={val => onChange(r.key, 'cpc', val)}
                                style={{
                                    width: '100%',
                                    backgroundColor: isReadOnly ? '#f5f5f5' : undefined,
                                    color: isReadOnly ? '#8c8c8c' : undefined
                                }}
                                prefix="$"
                                readOnly={isReadOnly}
                            />
                        );
                    }
                },
            ]
        },
        {
            title: '推演结果',
            children: [
                {
                    title: '销售额',
                    key: 'totalSales',
                    width: 85,
                    render: (_, r) => {
                        const metrics = calculateMetrics(r, price, profitMarginPreAd, landedCost);
                        return <Text style={{ fontSize: 14 }}>${metrics.totalSales.toLocaleString()}</Text>;
                    }
                },
                {
                    title: '广告预算',
                    key: 'adBudget',
                    width: 85,
                    render: (_, r) => {
                        const metrics = calculateMetrics(r, price, profitMarginPreAd, landedCost);
                        return <Text type="warning" style={{ fontSize: 14 }}>${metrics.adBudget.toFixed(0)}</Text>;
                    }
                },
                {
                    title: <Tooltip title="广告费 / 总销售额"><span>TACOS <InfoCircleOutlined /></span></Tooltip>,
                    key: 'tacos',
                    width: 75,
                    render: (_, r) => {
                        const metrics = calculateMetrics(r, price, profitMarginPreAd, landedCost);
                        return <Text style={{ fontSize: 14, color: metrics.tacos > profitMarginPreAd ? 'red' : 'inherit' }}>{metrics.tacos.toFixed(1)}%</Text>;
                    }
                },
                {
                    title: <Tooltip title="CPC / 单价 / 转化率"><span>ACOS <InfoCircleOutlined /></span></Tooltip>,
                    key: 'acos',
                    width: 80,
                    render: (_, r) => {
                        const metrics = calculateMetrics(r, price, profitMarginPreAd, landedCost);
                        // 场景7：目标ACOS反推CPC - 允许用户输入目标ACOS
                        if (r.key === '7') {
                            return (
                                <InputNumber
                                    size="small"
                                    min={0}
                                    max={100}
                                    value={r.targetAcos !== undefined ? r.targetAcos : metrics.acos}
                                    onChange={val => onChange(r.key, 'targetAcos', val)}
                                    style={{ width: '100%', backgroundColor: '#e6f7ff', fontSize: 14 }}
                                    suffix="%"
                                />
                            );
                        }
                        return <Text style={{ fontSize: 14 }}>{metrics.acos.toFixed(1)}%</Text>;
                    }
                },
                {
                    title: '净毛利',
                    key: 'netProfit',
                    width: 85,
                    render: (_, r) => {
                        const metrics = calculateMetrics(r, price, profitMarginPreAd, landedCost);
                        return <Text strong style={{ fontSize: 14, color: metrics.netProfit > 0 ? '#52c41a' : '#f5222d' }}>${metrics.netProfit.toFixed(0)}</Text>;
                    }
                },
                {
                    title: '净毛利率',
                    key: 'netMargin',
                    width: 85,
                    render: (_, r) => {
                        const metrics = calculateMetrics(r, price, profitMarginPreAd, landedCost);
                        // 场景4-6：目标净利相关 - 允许用户输入目标净毛利率
                        if (r.key === '4' || r.key === '5' || r.key === '6') {
                            return (
                                <InputNumber
                                    size="small"
                                    min={0}
                                    max={100}
                                    value={r.targetNetMargin !== undefined ? r.targetNetMargin : metrics.netMargin}
                                    onChange={val => onChange(r.key, 'targetNetMargin', val)}
                                    style={{ width: '100%', backgroundColor: '#e6f7ff', fontSize: 14 }}
                                    suffix="%"
                                />
                            );
                        }
                        return <Tag color={metrics.netMargin > 0 ? 'success' : 'error'} style={{ fontSize: 13 }}>{metrics.netMargin.toFixed(1)}%</Tag>;
                    }
                },
                {
                    title: 'ROI',
                    key: 'roi',
                    width: 70,
                    render: (_, r) => {
                        const metrics = calculateMetrics(r, price, profitMarginPreAd, landedCost);
                        return <Text style={{ fontSize: 14 }}>{metrics.roi.toFixed(0)}%</Text>;
                    }
                }
            ]
        }
    ];
};
